import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { useSetAtom } from "jotai";

import { api } from "../../../convex/_generated/api";
import { useSession } from "@/core/auth/useSession";
import { useModeDemo } from "@/core/demo";
import { abonnerAuxVentes, lesVentes } from "@/core/demo/argent";
import { paymentToastsAtom } from "@/core/state/paymentToasts";
import { haptic } from "@dozka/ui";

import { estMaVente } from "./paymentVolume";
import { statsDuJour } from "./statsDuJour";

/**
 * Son de paiement in-app — 100% côté client (cockpit ouvert).
 *
 * S'abonne au feed temps réel Convex `recentForMe` ; quand un nouvel event de
 * paiement apparaît, le pousse aux fêtes (`PaymentToasts`) qui jouent leurs
 * sons SI l'user a activé `paymentSoundEnabled`. On NE rejoue jamais l'historique : au montage on
 * marque tous les events présents comme « déjà vus ».
 *
 * ⚠️ Autoplay : les navigateurs bloquent l'audio tant qu'il n'y a pas eu
 * d'interaction. On « débloque » au 1er geste (pointerdown/keydown).
 *
 * 🔊 Le son qui « se coupe » et « se relance plusieurs fois » (Kayla,
 * 09-10-2026) — trois causes, trois gardes :
 *   · UNE voix par personne : chaque fenêtre ouverte du cockpit (onglets,
 *     navigateur ET app, téléphone) jouait le même son, décalé. Chacune
 *     réclame le son au serveur (`reclamerSon`) ; seule la première l'obtient,
 *     les autres montrent la vente sans la sonner. Un verrou du navigateur ne
 *     suffisait pas : il ne voit pas l'app.
 *   · On ne coupe jamais un son en cours : `currentTime = 0` le relançait.
 *   · Des sons SYNTHÉTISÉS (`shell/fete/moteur.ts`) au lieu de fichiers
 *     téléchargés au moment même de la vente : sur une connexion lente, la
 *     lecture rattrapait le téléchargement et s'arrêtait net.
 */

/**
 * 🔊 Cette fenêtre joue-t-elle le son de `cle` ? Seulement si elle PEUT jouer
 * (on y a déjà cliqué : le navigateur bloque l'audio avant) et si elle est la
 * première fenêtre de l'utilisateur à le réclamer. Réseau en panne : pas de son
 * plutôt qu'un son en double.
 */
export function useReclamerSon() {
  const reclamer = useMutation(api.paymentNotifications.reclamerSon);
  return useCallback(
    async (cle: string): Promise<boolean> => {
      if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return false;
      try {
        return await reclamer({ cle });
      } catch {
        return false;
      }
    },
    [reclamer],
  );
}

export function usePaymentSound() {
  const prefs = useQuery(api.paymentNotifications.getMyNotificationPrefs);
  // 🎬 En démo, les VRAIES ventes de l'équipe ne s'affichent pas (nom du fan,
  // de la créatrice et du vendeur à l'image) : seule la vente du fan fictif
  // se fête, plus bas.
  const enDemo = useModeDemo();
  const events = useQuery(api.paymentNotifications.recentForMe, enDemo ? "skip" : {});
  // Défaut ON (opt-out) : le son est activé d'office (cf getMyNotificationPrefs).
  const soundOn = prefs?.paymentSoundEnabled ?? true;
  const setToasts = useSetAtom(paymentToastsAtom);
  // 🔊 Qui je suis — pour reconnaître MA vente dans le feed de l'org.
  const { user } = useSession();
  const monWorkosUserId = user?.workosUserId ?? null;

  const reclamerSon = useReclamerSon();

  // null = pas encore initialisé (1er chargement → on ne joue pas le backlog).
  const seenRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!events) return;
    // 1er chargement : on enregistre l'existant sans jouer.
    if (seenRef.current === null) {
      seenRef.current = new Set(events.map((e) => e.id));
      return;
    }
    const seen = seenRef.current;
    const unseen = events.filter((e) => !seen.has(e.id));
    unseen.forEach((e) => seen.add(e.id));
    if (unseen.length === 0) return;

    // Retour haptique « paiement reçu » (mobile) — double le SFX d'un buzz
    // physique. No-op iOS/desktop ; coupé sous « réduire les animations ».
    haptic("success");

    // Chaque vente joue SA fête et son son : en grand la mienne, en petit
    // celles des autres. Une seule sonne par lot — la mienne s'il y en a une.
    // Le combo et le compteur du jour se lisent dans le feed, celle-ci comprise.
    const porteur = unseen.find((e) => estMaVente(e.sellerWorkosUserId, monWorkosUserId)) ?? unseen[0]!;
    const stats = statsDuJour(events, monWorkosUserId, Date.now());

    void (async () => {
      // Une seule fenêtre sonne : on réclame le son avant d'afficher, pour que
      // la fête sache tout de suite si elle joue ses sons ici.
      const sonIci = soundOn && (await reclamerSon(porteur.id));

      // Toast in-app TOUJOURS (le feed visuel demandé sur desktop) — `events`
      // est trié du plus récent au plus ancien. On préfixe et on cappe.
      setToasts((prev) =>
        [
          ...unseen.map((e) => ({
          id: e.id,
          tier: e.tier as "small" | "medium" | "large",
          amountCents: e.amountCents,
          currency: e.currency,
          creatorName: e.creatorName,
          itemSold: e.itemSold,
          soldByAi: e.soldByAi,
          soldByName: e.soldByName,
          conversationId: e.conversationId ?? null,
          fanName: e.fanName ?? null,
            estAMoi: estMaVente(e.sellerWorkosUserId, monWorkosUserId),
            sonIci: sonIci && e.id === porteur.id,
            ...stats,
          })),
          ...prev,
        ].slice(0, 4),
      );

    })();
  }, [events, soundOn, setToasts, monWorkosUserId, reclamerSon]);

  // 🎬 La vente du fan fictif : MA vente, fêtée en grand. Le combo et le
  // compteur du jour se lisent dans l'argent de la démo, celle-ci comprise.
  useEffect(() => {
    if (!enDemo) return;
    return abonnerAuxVentes((v) => {
      haptic("success");
      const maintenant = Date.now();
      const stats = statsDuJour(
        lesVentes(maintenant).map((x) => ({
          createdAt: Date.parse(x.created_at),
          amountCents: x.amount_cents ?? x.gross_usd_cents,
          currency: x.currency ?? "USD",
          sellerWorkosUserId: monWorkosUserId,
        })),
        monWorkosUserId,
        maintenant,
      );
      const amountCents = v.amount_cents ?? v.gross_usd_cents;
      setToasts((prev) =>
        [
          {
            id: v.order_id,
            tier: (amountCents < 2000 ? "small" : amountCents > 10000 ? "large" : "medium") as
              | "small"
              | "medium"
              | "large",
            amountCents,
            currency: v.currency ?? "USD",
            creatorName: v.creator_name,
            itemSold: v.product,
            soldByAi: false,
            soldByName: null,
            conversationId: null,
            fanName: v.fan_name,
            estAMoi: true,
            // Une seule fenêtre filme : pas besoin de réclamer le son au serveur.
            sonIci: soundOn && (navigator.userActivation?.hasBeenActive ?? true),
            ...stats,
          },
          ...prev,
        ].slice(0, 4),
      );
    });
  }, [enDemo, soundOn, setToasts, monWorkosUserId]);
}

/** Composant invisible : monte le listener au niveau du shell authentifié.
 *  (Retourne null — pas de JSX, vit dans ce fichier .ts.) */
export function PaymentSoundListener(): null {
  usePaymentSound();
  return null;
}
